import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import { supabase } from "@/lib/supabase";
import { useAdminI18n } from "@/hooks/useAdminI18n";

interface HomepageSection {
  id: string;
  name: string;
  isActive: boolean;
  order: number;
}

const DEFAULT_SECTIONS: HomepageSection[] = [
  { id: "hero", name: "Hero Slider", isActive: true, order: 1 },
  { id: "top-news", name: "Top News", isActive: true, order: 2 },
  { id: "latest", name: "Neueste Nachrichten", isActive: true, order: 3 },
  { id: "finanzen", name: "Finanzen", isActive: true, order: 4 },
  { id: "gesundheit", name: "Versicherung & Gesundheit", isActive: true, order: 5 },
  { id: "technologie", name: "Technologie & KI", isActive: true, order: 6 },
  { id: "schweiz", name: "Schweiz", isActive: true, order: 7 },
  { id: "arbeit", name: "Arbeit & Leben", isActive: true, order: 8 },
  { id: "deals", name: "Deals & Angebote", isActive: true, order: 9 },
  { id: "meinung", name: "Meinung & Kolumnen", isActive: true, order: 10 },
  { id: "newsletter", name: "Newsletter CTA", isActive: true, order: 11 },
];

export default function AdminHomepage() {
  const { t } = useAdminI18n();
  const [sections, setSections] = useState<HomepageSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [sliderArticles, setSliderArticles] = useState<{ id: string; title: string; imageUrl: string }[]>([]);

  // Load homepage_sections from Supabase settings table, or use defaults
  const fetchSections = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("settings")
      .select("value")
      .eq("key", "homepage_sections")
      .maybeSingle();

    if (!error && data?.value) {
      try {
        const parsed = JSON.parse(data.value as string) as HomepageSection[];
        setSections(parsed);
      } catch {
        setSections(DEFAULT_SECTIONS);
      }
    } else {
      setSections(DEFAULT_SECTIONS);
    }
    setLoading(false);
  }, []);

  // Load slider articles from Supabase
  const fetchSliderArticles = useCallback(async () => {
    const { data } = await supabase
      .from("articles")
      .select("id, title, image_url")
      .eq("show_on_homepage_slider", true)
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(5);

    if (data) {
      setSliderArticles(
        data.map((r) => ({
          id: r.id as string,
          title: r.title as string,
          imageUrl: r.image_url as string,
        }))
      );
    }
  }, []);

  useEffect(() => {
    fetchSections();
    fetchSliderArticles();
  }, [fetchSections, fetchSliderArticles]);

  const toggleSection = (id: string) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, isActive: !s.isActive } : s))
    );
  };

  const moveSection = (id: string, direction: "up" | "down") => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx === -1) return prev;
      if (direction === "up" && idx === 0) return prev;
      if (direction === "down" && idx === prev.length - 1) return prev;
      const newSections = [...prev];
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      [newSections[idx], newSections[swapIdx]] = [newSections[swapIdx], newSections[idx]];
      return newSections.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const { error } = await supabase
      .from("settings")
      .upsert({ key: "homepage_sections", value: JSON.stringify(sections) }, { onConflict: "key" });

    if (error) {
      setSaveError(error.message);
    } else {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
    setSaving(false);
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-hj-dark">{t("homepageAdmin.header.title")}</h1>
            <p className="text-sm text-hj-gray mt-0.5">{t("homepageAdmin.header.subtitle")}</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
          >
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                {t("homepageAdmin.saving")}
              </>
            ) : saveSuccess ? (
              <>
                <i className="ri-check-line"></i>
                {t("homepageAdmin.saved")}
              </>
            ) : (
              <>
                <i className="ri-save-line"></i>
                {t("homepageAdmin.save")}
              </>
            )}
          </button>
        </div>

        {saveError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            <strong>Fehler:</strong> {saveError}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-lg border border-hj-gray-border p-12 text-center">
            <div className="w-8 h-8 border-2 border-hj-red border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-hj-gray">Wird geladen...</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-hj-gray-border overflow-hidden">
            <div className="px-5 py-4 border-b border-hj-gray-border">
              <h2 className="text-sm font-bold text-hj-dark">{t("homepageAdmin.sections.title")}</h2>
              <p className="text-xs text-hj-gray mt-0.5">{t("homepageAdmin.sections.hint")}</p>
            </div>
            <div className="divide-y divide-hj-gray-border">
              {sections.map((section, idx) => (
                <div
                  key={section.id}
                  className={`px-5 py-4 flex items-center gap-4 ${!section.isActive ? "bg-gray-50" : ""}`}
                >
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveSection(section.id, "up")}
                      disabled={idx === 0}
                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-hj-gray-light transition-colors disabled:opacity-30"
                    >
                      <i className="ri-arrow-up-s-line text-hj-gray"></i>
                    </button>
                    <button
                      onClick={() => moveSection(section.id, "down")}
                      disabled={idx === sections.length - 1}
                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-hj-gray-light transition-colors disabled:opacity-30"
                    >
                      <i className="ri-arrow-down-s-line text-hj-gray"></i>
                    </button>
                  </div>

                  <div className="w-8 h-8 rounded-lg bg-hj-gray-light flex items-center justify-center text-xs font-bold text-hj-gray shrink-0">
                    {section.order}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${section.isActive ? "text-hj-dark" : "text-hj-gray"}`}>
                      {section.name}
                    </p>
                  </div>

                  <button
                    onClick={() => toggleSection(section.id)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${section.isActive ? "bg-hj-red" : "bg-gray-300"}`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${section.isActive ? "translate-x-5" : "translate-x-0.5"}`}
                    ></span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hero Slider Articles */}
        <div className="mt-6 bg-white rounded-lg border border-hj-gray-border p-5">
          <h2 className="text-sm font-bold text-hj-dark mb-1">{t("homepageAdmin.slider.title")}</h2>
          <p className="text-xs text-hj-gray mb-4">
            {t("homepageAdmin.slider.hint")}
          </p>
          {sliderArticles.length === 0 ? (
            <div className="text-center py-6 bg-hj-gray-light rounded-md">
              <i className="ri-slideshow-line text-2xl text-hj-gray mb-2 block"></i>
              <p className="text-sm text-hj-gray">{t("homepageAdmin.slider.empty")}</p>
              <p className="text-xs text-hj-gray mt-1">
                {t("homepageAdmin.slider.emptyHint")}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {sliderArticles.map((article, i) => (
                <div key={article.id} className="flex items-center gap-3 p-3 bg-hj-gray-light rounded-md">
                  <div className="w-12 h-8 rounded overflow-hidden shrink-0 bg-gray-200">
                    <img src={article.imageUrl} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-hj-dark truncate">{article.title}</p>
                  </div>
                  <span className="text-[10px] text-hj-gray shrink-0">#{i + 1}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
